import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    ArrowLeft, Save, X, Loader2, User, Home, Calendar,
    DollarSign, FileText, AlertCircle, Check, Upload,
    ChevronDown, Building, Key, Info
} from 'lucide-react';
import { useLandlordStore } from '@/stores/useLandlordStore';
import { useToast } from '@/contexts/ToastContext';
import apiClient from '@/services/apiClient';

interface Utility {
    name: string;
    type: 'fixed' | 'variable';
    amount: number | null;
}

interface UtilityResponsibility {
    [key: string]: 'Tenant' | 'Landlord';
}

interface FormData {
    tenantEmail: string;
    unitId: string;
    leaseStartDate: string;
    rentAmount: string;
    securityDeposit: string;
    houseRules: string;
    utilitiesResponsibilities: UtilityResponsibility;
}

const RegisterTenant: React.FC = () => {
    const { propertyId } = useParams<{ propertyId?: string }>();
    const navigate = useNavigate();
    const { success, error: toastError } = useToast();
    const { units, properties, fetchUnits, fetchProperties } = useLandlordStore();
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [showUnitDropdown, setShowUnitDropdown] = useState(false);
    const [availableUnits, setAvailableUnits] = useState<any[]>([]);
    const [selectedUnit, setSelectedUnit] = useState<any | null>(null);
    const [unitUtilities, setUnitUtilities] = useState<Utility[]>([]);

    const [formData, setFormData] = useState<FormData>({
        tenantEmail: '',
        unitId: '',
        leaseStartDate: new Date().toISOString().split('T')[0],
        rentAmount: '',
        securityDeposit: '',
        houseRules: '',
        utilitiesResponsibilities: {}
    });

    useEffect(() => {
        loadData();
    }, [propertyId]);

    const loadData = async () => {
        setLoading(true);
        try {
            if (propertyId) {
                await fetchUnits(propertyId);
                // Get available units for this property
                const propertyUnits = units.filter(u => u.property_id === propertyId && u.status === 'Available');
                setAvailableUnits(propertyUnits);
            }

            // Fetch property defaults for house rules
            if (propertyId) {
                try {
                    const response = await apiClient.get(`/properties/${propertyId}`);
                    if (response.data.default_house_rules) {
                        setFormData(prev => ({ ...prev, houseRules: response.data.default_house_rules }));
                    }
                } catch {
                    // Property fetch failed, continue without defaults
                }
            }
        } catch (err) {
            console.error('Failed to load data:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleUnitSelect = (unit: any) => {
        setSelectedUnit(unit);
        setFormData(prev => ({
            ...prev,
            unitId: unit.id,
            rentAmount: unit.rent_amount?.toString() || ''
        }));
        setShowUnitDropdown(false);

        // Parse utilities from unit
        let utilities: Utility[] = [];
        if (unit.billable_utilities) {
            if (Array.isArray(unit.billable_utilities)) {
                utilities = unit.billable_utilities;
            } else if (typeof unit.billable_utilities === 'string') {
                try {
                    utilities = JSON.parse(unit.billable_utilities);
                } catch {
                    // Parse failed
                }
            }
        }

        setUnitUtilities(utilities);

        // Initialize utilities responsibilities
        const responsibilities: UtilityResponsibility = {};
        utilities.forEach(util => {
            responsibilities[util.name] = 'Tenant';
        });
        setFormData(prev => ({ ...prev, utilitiesResponsibilities: responsibilities }));
    };

    const handleUtilityChange = (utilityName: string, payer: 'Tenant' | 'Landlord') => {
        setFormData(prev => ({
            ...prev,
            utilitiesResponsibilities: {
                ...prev.utilitiesResponsibilities,
                [utilityName]: payer
            }
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.tenantEmail || !formData.unitId || !formData.rentAmount || !formData.leaseStartDate) {
            toastError('Please fill in all required fields');
            return;
        }

        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.tenantEmail)) {
            toastError('Please enter a valid email address');
            return;
        }

        setSubmitting(true);
        try {
            const invitationData = {
                tenantEmail: formData.tenantEmail,
                unitId: formData.unitId,
                leaseStartDate: new Date(formData.leaseStartDate).toISOString(),
                rentAmount: parseFloat(formData.rentAmount),
                securityDeposit: parseFloat(formData.securityDeposit) || 0,
                utilitiesResponsibilities: formData.utilitiesResponsibilities,
                houseRules: formData.houseRules,
                propertyId: propertyId
            };

            await apiClient.post('/tenants/invite', invitationData);
            success('Tenant invitation sent successfully!');
            navigate(propertyId ? `/landlord/units/${propertyId}` : '/landlord/tenants');
        } catch (err: any) {
            if (err.response?.status === 409 && err.response?.data?.hasPendingInvite) {
                // Handle existing pending invitation
                const existingInviteId = err.response?.data?.existingInviteId;
                if (window.confirm(`A pending invitation already exists for this email. Would you like to revoke it and send a new invitation?`)) {
                    try {
                        await apiClient.delete(`/tenants/invitations/${existingInviteId}`);
                        // Retry sending
                        await apiClient.post('/tenants/invite', {
                            tenantEmail: formData.tenantEmail,
                            unitId: formData.unitId,
                            leaseStartDate: new Date(formData.leaseStartDate).toISOString(),
                            rentAmount: parseFloat(formData.rentAmount),
                            securityDeposit: parseFloat(formData.securityDeposit) || 0,
                            utilitiesResponsibilities: formData.utilitiesResponsibilities,
                            houseRules: formData.houseRules,
                            propertyId: propertyId
                        });
                        success('Tenant invitation sent successfully!');
                        navigate(propertyId ? `/landlord/units/${propertyId}` : '/landlord/tenants');
                    } catch (retryErr: any) {
                        toastError('Failed to send invitation: ' + (retryErr.response?.data?.message || 'Unknown error'));
                    }
                }
            } else {
                toastError(err.response?.data?.message || 'Failed to send invitation');
            }
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex items-center gap-4">
                <button
                    onClick={() => navigate(propertyId ? `/landlord/units/${propertyId}` : '/landlord/tenants')}
                    className="p-2 hover:bg-light-bg dark:hover:bg-dark-bg rounded-lg transition-colors"
                >
                    <ArrowLeft size={20} className="text-light-text dark:text-dark-text" />
                </button>
                <div>
                    <h1 className="text-2xl font-bold text-light-text dark:text-dark-text">Create Tenancy Agreement</h1>
                    <p className="text-sm text-light-subtle dark:text-dark-subtle">
                        Register a new tenant and send invitation
                    </p>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Tenant Details */}
                <div className="bg-light-card dark:bg-dark-card rounded-xl p-6 border border-light-border dark:border-dark-border">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="p-2 bg-primary/10 rounded-lg">
                            <User size={20} className="text-primary" />
                        </div>
                        <h2 className="text-lg font-bold text-light-text dark:text-dark-text">Prospective Tenant's Details</h2>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-light-text dark:text-dark-text mb-2">
                            Tenant's Email Address <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="email"
                            value={formData.tenantEmail}
                            onChange={(e) => setFormData(prev => ({ ...prev, tenantEmail: e.target.value }))}
                            placeholder="The email they will use to sign up"
                            className="w-full px-4 py-3 bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/20"
                            required
                        />
                        <p className="mt-2 text-sm text-light-subtle dark:text-dark-subtle">
                            The tenant will receive an invitation to this email address to complete their registration.
                        </p>
                    </div>
                </div>

                {/* Unit Selection */}
                <div className="bg-light-card dark:bg-dark-card rounded-xl p-6 border border-light-border dark:border-dark-border">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                            <Home size={20} className="text-blue-600 dark:text-blue-400" />
                        </div>
                        <h2 className="text-lg font-bold text-light-text dark:text-dark-text">Unit Selection</h2>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-light-text dark:text-dark-text mb-2">
                            Unit to Assign <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                            <button
                                type="button"
                                onClick={() => setShowUnitDropdown(!showUnitDropdown)}
                                className="w-full flex items-center justify-between px-4 py-3 bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border rounded-lg text-left hover:border-primary/50 transition-colors"
                            >
                                <span className={selectedUnit ? 'text-light-text dark:text-dark-text' : 'text-light-subtle dark:text-dark-subtle'}>
                                    {selectedUnit
                                        ? `${selectedUnit.unit_number} (${selectedUnit.unit_type})`
                                        : 'Tap to select an available unit'}
                                </span>
                                <ChevronDown size={18} className="text-light-subtle dark:text-dark-subtle" />
                            </button>

                            {showUnitDropdown && availableUnits.length > 0 && (
                                <div className="absolute z-50 w-full mt-2 bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg shadow-lg max-h-64 overflow-y-auto">
                                    {availableUnits.map(unit => (
                                        <button
                                            key={unit.id}
                                            type="button"
                                            onClick={() => handleUnitSelect(unit)}
                                            className="w-full flex items-center justify-between px-4 py-3 hover:bg-light-bg dark:hover:bg-dark-bg transition-colors text-left"
                                        >
                                            <div>
                                                <p className="font-medium text-light-text dark:text-dark-text">{unit.unit_number}</p>
                                                <p className="text-sm text-light-subtle dark:text-dark-subtle">{unit.unit_type}</p>
                                            </div>
                                            <div className="text-right">
                                                <p className="font-medium text-light-text dark:text-dark-text">
                                                    KES {unit.rent_amount?.toLocaleString() || '0'}/month
                                                </p>
                                                <p className="text-sm text-light-subtle dark:text-dark-subtle">
                                                    {unit.bedrooms || 'Studio'} bed
                                                </p>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}

                            {showUnitDropdown && availableUnits.length === 0 && (
                                <div className="absolute z-50 w-full mt-2 bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg shadow-lg p-4 text-center">
                                    <p className="text-light-subtle dark:text-dark-subtle">No available units found</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Lease & Financial Terms */}
                <div className="bg-light-card dark:bg-dark-card rounded-xl p-6 border border-light-border dark:border-dark-border">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="p-2 bg-green-50 dark:bg-green-900/20 rounded-lg">
                            <DollarSign size={20} className="text-green-600 dark:text-green-400" />
                        </div>
                        <h2 className="text-lg font-bold text-light-text dark:text-dark-text">Lease & Financial Terms</h2>
                    </div>

                    <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-light-text dark:text-dark-text mb-2">
                                    Monthly Rent (KES) <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="number"
                                    value={formData.rentAmount}
                                    onChange={(e) => setFormData(prev => ({ ...prev, rentAmount: e.target.value }))}
                                    placeholder="Auto-filled from selected unit"
                                    className="w-full px-4 py-3 bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/20"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-light-text dark:text-dark-text mb-2">
                                    Security Deposit (KES)
                                </label>
                                <input
                                    type="number"
                                    value={formData.securityDeposit}
                                    onChange={(e) => setFormData(prev => ({ ...prev, securityDeposit: e.target.value }))}
                                    placeholder="e.g., 30000"
                                    className="w-full px-4 py-3 bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/20"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-light-text dark:text-dark-text mb-2">
                                Lease Start Date <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="date"
                                value={formData.leaseStartDate}
                                onChange={(e) => setFormData(prev => ({ ...prev, leaseStartDate: e.target.value }))}
                                className="w-full px-4 py-3 bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/20"
                                required
                            />
                        </div>
                    </div>
                </div>

                {/* Utility Responsibilities */}
                {unitUtilities.length > 0 && (
                    <div className="bg-light-card dark:bg-dark-card rounded-xl p-6 border border-light-border dark:border-dark-border">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="p-2 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                                <Key size={20} className="text-orange-600 dark:text-orange-400" />
                            </div>
                            <h2 className="text-lg font-bold text-light-text dark:text-dark-text">Utility Responsibilities</h2>
                        </div>

                        <div className="space-y-3">
                            {unitUtilities.map(utility => (
                                <div key={utility.name} className="flex items-center justify-between p-4 bg-light-bg dark:bg-dark-bg rounded-lg">
                                    <div>
                                        <p className="font-medium text-light-text dark:text-dark-text">{utility.name}</p>
                                        <p className="text-sm text-light-subtle dark:text-dark-subtle">
                                            {utility.type === 'fixed'
                                                ? `Fixed: KES ${utility.amount?.toLocaleString() || 0}/month`
                                                : 'Variable Cost (Based on usage)'}
                                        </p>
                                    </div>
                                    <div className="flex gap-2">
                                        <button
                                            type="button"
                                            onClick={() => handleUtilityChange(utility.name, 'Tenant')}
                                            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                                                formData.utilitiesResponsibilities[utility.name] === 'Tenant'
                                                    ? 'bg-primary text-white'
                                                    : 'bg-light-card dark:bg-dark-card text-light-text dark:text-dark-text border border-light-border dark:border-dark-border'
                                            }`}
                                        >
                                            Tenant
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => handleUtilityChange(utility.name, 'Landlord')}
                                            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                                                formData.utilitiesResponsibilities[utility.name] === 'Landlord'
                                                    ? 'bg-primary text-white'
                                                    : 'bg-light-card dark:bg-dark-card text-light-text dark:text-dark-text border border-light-border dark:border-dark-border'
                                            }`}
                                        >
                                            Landlord
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* House Rules */}
                <div className="bg-light-card dark:bg-dark-card rounded-xl p-6 border border-light-border dark:border-dark-border">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="p-2 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                            <FileText size={20} className="text-purple-600 dark:text-purple-400" />
                        </div>
                        <h2 className="text-lg font-bold text-light-text dark:text-dark-text">House Rules & Clauses</h2>
                    </div>

                    <div>
                        <textarea
                            value={formData.houseRules}
                            onChange={(e) => setFormData(prev => ({ ...prev, houseRules: e.target.value }))}
                            placeholder="Enter custom rules or use the property defaults..."
                            rows={6}
                            className="w-full px-4 py-3 bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none"
                        />
                        <p className="mt-2 text-sm text-light-subtle dark:text-dark-subtle">
                            These rules will be included in the tenancy agreement.
                        </p>
                    </div>
                </div>

                {/* Submit */}
                <div className="flex gap-4">
                    <button
                        type="button"
                        onClick={() => navigate(propertyId ? `/landlord/units/${propertyId}` : '/landlord/tenants')}
                        className="flex-1 flex items-center justify-center gap-2 px-6 py-3 border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text hover:bg-light-bg dark:hover:bg-dark-bg transition-colors font-medium"
                    >
                        <X size={18} />
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={submitting}
                        className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                    >
                        {submitting ? (
                            <>
                                <Loader2 size={18} className="animate-spin" />
                                Sending Invitation...
                            </>
                        ) : (
                            <>
                                <Check size={18} />
                                Send Invitation
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default RegisterTenant;